import { useCallback, useEffect, useRef, useState } from 'react';
import { Recipe } from '../../api/Recipe';
import {
  uniqueFavoriteRecipes,
  writeAuthorFavoritesCache,
} from '../../domains/favorites/cache';

interface UseFavoriteCacheStateResult {
  favorites: Recipe[];
  favoritesRef: React.RefObject<Recipe[]>;
  setCachedFavorites: (favorites: Recipe[]) => void;
  clearFavorites: () => void;
}

const EMPTY_FAVORITES: Recipe[] = [];

const normalizeFavorites = (favorites: Recipe[]) => uniqueFavoriteRecipes(favorites);

const useFavoriteCacheState = (authorId?: number): UseFavoriteCacheStateResult => {
  const [favorites, setFavorites] = useState<Recipe[]>(EMPTY_FAVORITES);
  const favoritesRef = useRef<Recipe[]>(EMPTY_FAVORITES);

  const applyFavoritesState = useCallback((nextFavorites: Recipe[]) => {
    favoritesRef.current = nextFavorites;
    setFavorites(nextFavorites);
  }, []);

  useEffect(() => {
    favoritesRef.current = favorites;
  }, [favorites]);

  const persistCache = useCallback((nextFavorites: Recipe[]) => {
    if (!authorId) {
      return;
    }

    writeAuthorFavoritesCache(authorId, nextFavorites);
  }, [authorId]);

  const setCachedFavorites = useCallback((nextFavorites: Recipe[]) => {
    const normalizedFavorites = normalizeFavorites(nextFavorites);
    applyFavoritesState(normalizedFavorites);
    persistCache(normalizedFavorites);
  }, [applyFavoritesState, persistCache]);

  const clearFavorites = useCallback(() => {
    applyFavoritesState(EMPTY_FAVORITES);
  }, [applyFavoritesState]);

  return {
    favorites,
    favoritesRef,
    setCachedFavorites,
    clearFavorites,
  };
};

export default useFavoriteCacheState;
